// Dynamic landing page from Strapi, used for both /products/:slug and
// /solutions/:slug. The two routes share the same render shell; only the
// fetcher and a tiny copy difference change.
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, AlertCircle, ArrowLeft, ArrowUpRight, ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteLayout from "@/components/site/SiteLayout";
import Icon from "@/components/site/Icon";
import { fetchProductPage, fetchSolutionPage, flatten } from "@/lib/cmsExtras";
import { useInternalLinks } from "@/lib/internalLinks";

function FeatureGrid({ items, testId }) {
  if (!items?.length) return null;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid={testId}>
      {items.map((f, i) => (
        <div key={i} className="rounded-2xl border border-border bg-white p-5">
          <div className="h-11 w-11 grid place-items-center rounded-xl bg-primary/10 text-primary">
            <Icon name={f.iconKey} className="h-5 w-5" />
          </div>
          <div className="mt-4 font-display font-bold text-primary tracking-tight">{f.title}</div>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
        </div>
      ))}
    </div>
  );
}

function Body({ items, skipHref, linkify }) {
  if (!items?.length) return null;
  return (
    <div className="space-y-10">
      {items.map((b, i) => (
        <section key={i}>
          <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">{b.h}</h2>
          {Array.isArray(b.p) && b.p.length > 0 && (
            <div className="mt-4 space-y-3 text-base md:text-lg text-primary/85 leading-relaxed max-w-3xl">
              {b.p.map((p, j) => <p key={j}>{linkify ? linkify(p, { skipHref, key: `il-cms-${i}-${j}` }) : p}</p>)}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function Faqs({ items }) {
  if (!items?.length) return null;
  return (
    <section className="mt-16">
      <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">FAQs</h2>
      <div className="mt-5 divide-y divide-border border border-border rounded-2xl bg-white max-w-3xl">
        {items.map((f, i) => (
          <details key={i} className="group px-5 py-4">
            <summary className="cursor-pointer font-display font-bold text-primary">{f.q}</summary>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

function CtaBanner({ cta }) {
  if (!cta?.title) return null;
  return (
    <section className="mt-20 rounded-3xl bg-primary text-primary-foreground p-10 md:p-14">
      <div className="flex flex-wrap items-end gap-6 justify-between">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl md:text-4xl font-bold tracking-tight leading-tight">{cta.title}</h2>
          {cta.subtitle && <p className="mt-3 text-base text-white/75">{cta.subtitle}</p>}
        </div>
        {cta.buttonLabel && cta.buttonHref && (
          <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
            <Link to={cta.buttonHref}>
              {cta.buttonLabel} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        )}
      </div>
    </section>
  );
}

function Hero({ hero, eyebrow }) {
  if (!hero?.title) return null;
  return (
    <section className="dark-hero py-16 md:py-24 border-b border-border">
      <div className="container-cv grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          {(hero.eyebrow || eyebrow) && (
            <span className="section-eyebrow text-accent before:bg-accent">{hero.eyebrow || eyebrow}</span>
          )}
          <h1 className="mt-3 font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {hero.title}
          </h1>
          {hero.subtitle && (
            <p className="mt-5 text-base md:text-lg text-white/70 max-w-2xl">{hero.subtitle}</p>
          )}
          {(hero.primaryCtaLabel || hero.secondaryCtaLabel) && (
            <div className="mt-8 flex flex-wrap gap-3">
              {hero.primaryCtaLabel && hero.primaryCtaHref && (
                <Button asChild className="h-12 px-6 bg-accent hover:bg-accent/90 text-primary font-bold">
                  <Link to={hero.primaryCtaHref}>{hero.primaryCtaLabel} <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              )}
              {hero.secondaryCtaLabel && hero.secondaryCtaHref && (
                <Button asChild variant="outline" className="h-12 px-6 border-white/30 text-white hover:bg-white hover:text-[hsl(207_74%_21%)] font-semibold bg-white/[0.04]">
                  <Link to={hero.secondaryCtaHref}>{hero.secondaryCtaLabel}</Link>
                </Button>
              )}
            </div>
          )}
        </div>
        {hero.image && (
          <div className="lg:col-span-5">
            <div className="light-surface rounded-3xl border border-border bg-white overflow-hidden">
              <img src={hero.image} alt={hero.title} className="w-full h-auto" loading="eager" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function NotFound({ kind, backTo, backLabel }) {
  return (
    <SiteLayout title={`${kind} not found`}>
      <div className="container-cv py-24 text-center">
        <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground" />
        <h1 className="mt-4 font-display text-2xl font-bold text-primary">{kind} not found</h1>
        <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
          When CMS_PROVIDER=strapi and a matching <code className="rounded bg-secondary px-1.5 py-0.5 text-[11px]">{kind.toLowerCase().replace(" ", "-")}</code> is published in Strapi, it&apos;ll render here automatically.
        </p>
        <Link to={backTo} className="mt-5 inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </Link>
      </div>
    </SiteLayout>
  );
}

// ----- Page wrappers -----

export function DynamicProductPage() {
  return <CmsLanding kind="Product" fetcher={fetchProductPage} backTo="/" backLabel="Back to home" />;
}

export function DynamicSolutionPage() {
  return <CmsLanding kind="Solution" fetcher={fetchSolutionPage} backTo="/" backLabel="Back to home" />;
}

function CmsLanding({ kind, fetcher, backTo, backLabel }) {
  const { slug } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const linkify = useInternalLinks();

  useEffect(() => {
    setLoading(true);
    fetcher(slug)
      .then((res) => setDoc(flatten(res.data)))
      .catch((e) => setError(e.status === 404 ? "not_found" : e.message))
      .finally(() => setLoading(false));
  }, [slug, fetcher]);

  if (loading) {
    return (
      <SiteLayout>
        <div className="container-cv py-20 inline-flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      </SiteLayout>
    );
  }
  if (error === "not_found" || !doc) {
    return <NotFound kind={kind} backTo={backTo} backLabel={backLabel} />;
  }

  const hero = doc.hero || null;
  const isSolution = kind === "Solution";

  // Article / Product JSON-LD, boosts AEO so AI search engines understand
  // what kind of entity this page represents.
  const ldEntity = isSolution
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        name: doc.name,
        serviceType: doc.persona,
        provider: { "@type": "Organization", name: "CoSec Global" },
        description: doc.seoDescription || doc.tagline,
      }
    : {
        "@context": "https://schema.org",
        "@type": "Product",
        name: doc.name,
        description: doc.seoDescription || doc.tagline,
        brand: { "@type": "Brand", name: "CoSec Global" },
        image: hero?.image ? [hero.image] : undefined,
      };

  return (
    <SiteLayout
      title={`${doc.name || hero?.title || kind}`}
      description={doc.seoDescription || doc.tagline || ""}
      image={hero?.image}
      type={isSolution ? "website" : "product"}
      faq={Array.isArray(doc.faqs) ? doc.faqs : undefined}
      breadcrumbs={[
        { name: "Home", href: "https://www.cosecglobal.com/" },
        { name: isSolution ? "Solutions" : "Products", href: `https://www.cosecglobal.com/${isSolution ? "solutions" : "products"}/${slug}` },
        { name: doc.name || slug, href: `https://www.cosecglobal.com/${isSolution ? "solutions" : "products"}/${slug}` },
      ]}
      jsonLd={ldEntity}
    >
      <Hero hero={hero} eyebrow={isSolution ? (doc.persona ? `For ${doc.persona}` : "Solution") : "Product"} />

      <div className="container-cv py-16 md:py-20 space-y-16">
        {isSolution && doc.painPoints?.length > 0 && (
          <section>
            <span className="section-eyebrow">Pain points</span>
            <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-primary tracking-tight">What we hear most.</h2>
            <div className="mt-7"><FeatureGrid items={doc.painPoints} testId="solution-pain-points" /></div>
          </section>
        )}

        {isSolution && doc.outcomes?.length > 0 && (
          <section>
            <span className="section-eyebrow">Outcomes</span>
            <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-primary tracking-tight">What changes after CoSec.</h2>
            <div className="mt-7"><FeatureGrid items={doc.outcomes} testId="solution-outcomes" /></div>
          </section>
        )}

        {!isSolution && doc.features?.length > 0 && (
          <section>
            <span className="section-eyebrow">Features</span>
            <h2 className="mt-3 font-display text-2xl md:text-4xl font-bold text-primary tracking-tight">Built around the work.</h2>
            <div className="mt-7"><FeatureGrid items={doc.features} testId="product-features" /></div>
          </section>
        )}

        <Body items={doc.body} skipHref={`/${isSolution ? "solutions" : "products"}/${slug}`} linkify={linkify} />

        {doc.quote?.q && (
          <section className="rounded-3xl border border-border bg-secondary/40 p-8 md:p-12 max-w-3xl">
            <Quote className="h-7 w-7 text-accent" />
            <p className="mt-4 font-display text-xl md:text-2xl text-primary leading-snug">&ldquo;{doc.quote.q}&rdquo;</p>
            <div className="mt-5 text-sm">
              <div className="font-bold text-primary">{doc.quote.n}</div>
              {doc.quote.r && <div className="text-muted-foreground">{doc.quote.r}</div>}
            </div>
          </section>
        )}

        <Faqs items={doc.faqs} />

        <CtaBanner cta={doc.cta} />

        {(doc.relatedProducts?.length > 0 || doc.relatedSolutions?.length > 0) && (
          <section className="border-t border-border pt-12">
            <span className="section-eyebrow">Related</span>
            <div className="mt-4 flex flex-wrap gap-3">
              {(doc.relatedProducts || []).map((s) => (
                <Link key={`p-${s}`} to={`/products/${s}`} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">
                  Product: {s} <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              ))}
              {(doc.relatedSolutions || []).map((s) => (
                <Link key={`s-${s}`} to={`/solutions/${s}`} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent">
                  Solution: {s} <ArrowUpRight className="h-3.5 w-3.5" />
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}
